import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Reveal } from '../Reveal';
import { Note, UnderlineMark } from '../Marks';

/**
 * Three ways to read the same six letters. Each step lights up the letters it
 * owns and dims the rest, so the wordplay is something you watch rather than
 * something you read an explanation of.
 */
interface Step {
  id: string;
  word: string;
  /** Letter indices this step claims. */
  lit: number[];
  /** Letters swapped out for this step, by index. */
  swap?: Record<number, string>;
  color: string;
  caption: string;
}
const LETTERS = ['S', 'M', 'Å', 'K', 'E', 'N'];
const STEPS: Step[] = [{
  id: 'smaken',
  word: 'Smaken',
  lit: [0, 1, 2, 3, 4, 5],
  swap: {
    2: 'A'
  },
  color: '#2015B8',
  caption: 'Din smak. Min smak. Båda får plats.'
}, {
  id: 'smaland',
  word: 'Småland',
  lit: [0, 1, 2],
  color: '#E42616',
  caption: 'Ett landskap. Inte hela landet.'
}, {
  id: 'smaken-nickname',
  word: 'Småken',
  lit: [0, 1, 2, 3, 4, 5],
  color: '#111111',
  caption: 'Kompisen som vet var det är gott.'
}];
export function NameStory() {
  const [active, setActive] = useState(0);
  const [locked, setLocked] = useState(false);
  const reduced = useReducedMotion();

  // Cycles on its own until someone takes over.
  useEffect(() => {
    if (locked || reduced) return;
    const id = window.setInterval(() => setActive(i => (i + 1) % STEPS.length), 4000);
    return () => window.clearInterval(id);
  }, [locked, reduced]);
  const step = STEPS[active];
  const pick = (index: number) => {
    setLocked(true);
    setActive(index);
  };
  return <section aria-labelledby="namnet" className="border-y border-black/12" data-id="element-551" data-mp="2wkqh261rohfy">
      <div className="mx-auto max-w-page px-5 py-16 md:px-8 md:py-24" data-id="element-552" data-mp="2hlwhwl151y8f">
        <div className="grid gap-16 lg:grid-cols-[minmax(0,1.15fr),minmax(0,1fr)] lg:gap-24" data-id="element-553" data-mp="2hor3ifvw1p3f">
          {/* ------------------------------ The name ------------------------------ */}
          <div data-id="element-554" data-mp="23iz3vxm7ugzc">
            <Reveal data-id="element-555" data-mp="1yq60pe6ebgpd">
              <p className="text-[11px] font-bold uppercase tracking-wide3 text-black/40" data-id="element-556" data-mp="1ypmfnolg8c74">Namnet</p>
              <h2 id="namnet" className="mt-5 font-display text-[2.1rem] font-light leading-[1.12] sm:text-[2.8rem]" data-id="element-557" data-mp="1tqjo96ye64hm">
                Smaken är som baken. Delad.
                <br data-id="element-558" data-mp="2mg0crtiw518k" />
                <span className="text-rod" data-id="element-559" data-mp="2mefebstggy5f">Vi tog de bästa bitarna.</span>
              </h2>
              <p className="mt-5 max-w-md text-lg leading-relaxed text-black/65" data-id="element-560" data-mp="2hhyuhatah5y5">
                Kalla oss Småken. Vi tipsar, guidar och beställer — som en kompis med bra smak.
              </p>
            </Reveal>

            {/* Interactive wordmark */}
            <div className="mt-12 border-t border-black/12 pt-10" data-id="element-561" data-mp="1125oalnqsy3f">
              <p className="display-xl flex select-none leading-none tracking-tight" style={{
              fontSize: 'clamp(2.75rem, 9vw, 5rem)'
            }} aria-label="Småken" data-id="element-562" data-mp="110b1f1swuvwp">
                {LETTERS.map((letter, i) => {
                const lit = step.lit.includes(i);
                const char = step.swap?.[i] ?? letter;
                return <span key={i} aria-hidden="true" className="transition-colors duration-[250ms] ease-out" style={{
                  color: lit ? step.color : 'rgba(17,17,17,0.13)'
                }} data-id="element-563" data-mp="3hmr6wyc8uxr3" data-mpi={i}>
                      <AnimatePresence mode="popLayout" initial={false} data-id="element-564" data-mp="3venxgxy9kzld">
                        <motion.span key={char} initial={reduced ? false : {
                      opacity: 0,
                      y: 5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={reduced ? undefined : {
                      opacity: 0,
                      y: -5
                    }} transition={{
                      duration: 0.18,
                      ease: [0.23, 1, 0.32, 1]
                    }} className="inline-block" data-id="element-565" data-mp="4az6b83maqff">
                          {char}
                        </motion.span>
                      </AnimatePresence>
                    </span>;
              })}
              </p>

              <ol className="mt-9 flex flex-wrap items-baseline gap-x-9 gap-y-4" role="tablist" data-id="element-566" data-mp="ec041hvex279">
                {STEPS.map((s, i) => <li key={s.id} data-id="element-567" data-mp="3iqrjdac9nrjp" data-mpi={i}>
                    <button role="tab" aria-selected={i === active} onClick={() => pick(i)} onMouseEnter={() => pick(i)} className="group relative flex items-baseline gap-2.5 pb-2" data-id="element-568" data-mp="3irszvuc1x68t">
                      <span className="text-[11px] font-bold tracking-wide2 transition-colors duration-150 ease-out" style={{
                    color: i === active ? s.color : 'rgba(17,17,17,0.3)'
                  }} data-id="element-569" data-mp="3mrbigjgeh58h">
                        0{i + 1}
                      </span>
                      <span className="font-display text-2xl transition-colors duration-150 ease-out sm:text-3xl" style={{
                    color: i === active ? '#111111' : 'rgba(17,17,17,0.35)'
                  }} data-id="element-570" data-mp="2kvf8laz1az4f">
                        {s.word}
                      </span>
                      {i === active && <UnderlineMark className="absolute -bottom-0.5 left-8 h-2.5 w-[calc(100%-2rem)]" color={s.color} data-id="element-571" data-mp="33p1akrc6vgnb" />}
                    </button>
                  </li>)}
              </ol>

              <div className="mt-6 min-h-[3rem]" data-id="element-572" data-mp="2ynllmo6w4pzn">
                <AnimatePresence mode="wait" initial={false} data-id="element-573" data-mp="1ian5q771gx69">
                  <motion.p key={step.id} initial={reduced ? false : {
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={reduced ? undefined : {
                  opacity: 0,
                  y: -6
                }} transition={{
                  duration: 0.2,
                  ease: [0.23, 1, 0.32, 1]
                }} className="max-w-md font-display text-xl leading-snug text-black/75 sm:text-2xl" data-id="element-574" data-mp="1i9bxnv6k939c">
                    {step.caption}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>
          </div>

          <Reveal delay={0.08} data-id="element-575" data-mp="369qx8hya1ziy">
            <div className="lg:pt-16" data-id="element-576" data-mp="326uaqg5m9j9q">
              <Note className="block !text-2xl sm:!text-3xl" data-id="element-577" data-mp="325rlecnxzovx">Säg Småken. Det gör vi.</Note>
              <p className="mt-6 max-w-sm text-lg leading-relaxed text-black/65" data-id="element-578" data-mp="2x8soxngt35uk">
                Märket är ritat för hand. Sneda bokstäver med flit — precis som ställena vi väljer, gjorda
                av någon och inte av en maskin.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}